import { store } from "../store";
import axios from "axios";
import history from "../helpers/navigation";
import { setToastOpen } from "components/Toast/toastAlertSlice";
import { NetworkError } from "./error_response_json";
import { processError } from "helpers/ServiceErrorUtility";
import { setLoaderOpen } from "components/Loader/loaderSlice";
import { setLoaderClose } from "components/Loader/loaderSlice";
import { RefreshToken } from "./apis";

let refreshAccessTokenPromise = null;

export const getStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user"));
  } catch (error) {
    return null;
  }
};

const updateStoredTokens = (tokens = {}) => {
  const user = getStoredUser();
  if (!user || !tokens.access) return null;

  const updatedUser = {
    ...user,
    access: tokens.access,
    refresh: tokens.refresh || user.refresh,
  };

  localStorage.setItem("user", JSON.stringify(updatedUser));
  return updatedUser;
};

const redirectToLogin = () => {
  localStorage.clear();
  history.go("/authentication/sign-in/illustration");
};

const refreshAccessToken = async () => {
  const user = getStoredUser();

  if (!user?.refresh) {
    throw new Error("Refresh token is missing");
  }

  if (!refreshAccessTokenPromise) {
    refreshAccessTokenPromise = axios
      .post(`${base_url}${RefreshToken}`, { refresh: user.refresh }, withoutAuthConfig)
      .then((response) => {
        const access = response?.data?.access;
        if (!access) {
          throw new Error("Refresh response did not include an access token");
        }
        updateStoredTokens({
          access,
          refresh: response?.data?.refresh,
        });
        return access;
      })
      .finally(() => {
        refreshAccessTokenPromise = null;
      });
  }

  return refreshAccessTokenPromise;
};

export const authFetch = async (url, options = {}) => {
  const buildOptions = () => {
    const user = getStoredUser();
    return {
      ...options,
      headers: {
        ...(options.headers || {}),
        "Org-Id": user?.org_id,
        Authorization: `Bearer ${user?.access}`,
      },
    };
  };

  let response = await fetch(url, buildOptions());

  if (response.status === 401) {
    try {
      await refreshAccessToken();
      response = await fetch(url, buildOptions());
    } catch (error) {
      store.dispatch(
        setToastOpen({ isOpen: true, message: "Session expired. Please sign in again." })
      );
      redirectToLogin();
    }
  }

  return response;
};

const authConfigFunc = () => {
  var user = getStoredUser();
  return {
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "Org-Id": user?.org_id,
      Authorization: `Bearer ${user?.access}`,
    },
  };
};

const authConfigWithFormDataFunc = () => {
  var user = getStoredUser();

  return {
    headers: {
      Accept: "application/json",
      "Content-Type": "multipart/form-data",
      "Org-Id": user?.org_id,
      Authorization: `Bearer ${user?.access}`,
    },
  };
};

const withoutAuthConfig = {
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
};

export const base_url =
  process.env.REACT_APP_API_BASE_URL || window.location.origin;

console.log("API BASE URL:", process.env);


const services = {
  get: async (url, isRequireAuth) => {
    try {
      store.dispatch(setLoaderOpen());
      const request = () =>
        axios.get(
          `${base_url}${url}`,
          isRequireAuth ? authConfigFunc() : withoutAuthConfig
        );
      const response = await request();
      store.dispatch(setLoaderClose());
      return successResponse(response);
    } catch (error) {
      store.dispatch(setLoaderClose());
      return handleRequestError(error, isRequireAuth, () =>
        axios.get(`${base_url}${url}`, authConfigFunc())
      );
    }
  },
  post: async (url, data, isRequireAuth, isFormData) => {
    try {
      store.dispatch(setLoaderOpen());
      if (isRequireAuth) {
        if (isFormData) {
          data.append(
            "org",
            `${getStoredUser()?.org_id}`
          );
        } else {
          data.org = `${getStoredUser()?.org_id}`;
        }
      }
      const request = () =>
        axios.post(
          `${base_url}${url}`,
          data,
          isRequireAuth
            ? isFormData
              ? authConfigWithFormDataFunc()
              : authConfigFunc()
            : withoutAuthConfig
        );
      const response = await request();

      store.dispatch(setLoaderClose());
      return successResponse(response);
    } catch (error) {
      store.dispatch(setLoaderClose());
      return handleRequestError(error, isRequireAuth, () =>
        axios.post(
          `${base_url}${url}`,
          data,
          isFormData ? authConfigWithFormDataFunc() : authConfigFunc()
        )
      );
    }
  },
  put: async (url, data, isRequireAuth) => {
    try {
      store.dispatch(setLoaderOpen());
      const request = () =>
        axios.put(
          `${base_url}${url}`,
          data,
          isRequireAuth ? authConfigFunc() : withoutAuthConfig
        );
      const response = await request();

      store.dispatch(setLoaderClose());
      return successResponse(response);
    } catch (error) {
      store.dispatch(setLoaderClose());
      return handleRequestError(error, isRequireAuth, () =>
        axios.put(`${base_url}${url}`, data, authConfigFunc())
      );
    }
  },
  patch: async (url, data, isRequireAuth, isFormData) => {
    try {
      store.dispatch(setLoaderOpen());

      const request = () =>
        axios.patch(
          `${base_url}${url}`,
          data,
          isRequireAuth
            ? isFormData
              ? authConfigWithFormDataFunc()
              : authConfigFunc()
            : withoutAuthConfig
        );
      const response = await request();

      store.dispatch(setLoaderClose());
      return successResponse(response);
    } catch (error) {
      store.dispatch(setLoaderClose());
      return handleRequestError(error, isRequireAuth, () =>
        axios.patch(
          `${base_url}${url}`,
          data,
          isFormData ? authConfigWithFormDataFunc() : authConfigFunc()
        )
      );
    }
  },
  delete: async (url, isRequireAuth) => {
    try {

      const deleteConfirmation = confirm("Are you sure want to delete ?")
      if(deleteConfirmation){
        store.dispatch(setLoaderOpen());
      const request = () =>
        axios.delete(
          `${base_url}${url}`,
          isRequireAuth ? authConfigFunc() : withoutAuthConfig
        );
      const response = await request();

      store.dispatch(setLoaderClose());
      return successResponse(response);
      }
    } catch (error) {
      store.dispatch(setLoaderClose());
      return handleRequestError(error, isRequireAuth, () =>
        axios.delete(`${base_url}${url}`, authConfigFunc())
      );
    }
  },
};

const handleRequestError = async (error, isRequireAuth, retryRequest) => {
  if (isRequireAuth && error?.response?.status === 401) {
    try {
      await refreshAccessToken();
      const retryResponse = await retryRequest();
      return successResponse(retryResponse);
    } catch (refreshError) {
      store.dispatch(
        setToastOpen({ isOpen: true, message: "Session expired. Please sign in again." })
      );
      redirectToLogin();
      return errorResponse(error, { skipAuthRedirect: true, skipAuthToast: true });
    }
  }

  return errorResponse(error);
};

const errorResponse = (error, options = {}) => {
  const result = {
    success: false,
    data: null,
    code: 0,
  };

  if (error?.response?.status == 401) {
    if (!options.skipAuthToast) {
      store.dispatch(
        setToastOpen({ isOpen: true, message: "Authentication Error!" })
      );
    }
    if (!options.skipAuthRedirect) {
      redirectToLogin();
    }
  } else if (error?.response?.status == 500) {
    store.dispatch(
      setToastOpen({ isOpen: true, message: "Internal Server Error" })
    );
  } else if (error?.response?.status == 400) {
    if (error?.response?.data) {
      processError(error?.response?.data);
      store.dispatch(
        setToastOpen({
          isOpen: true,
          message: processError(error?.response?.data),
        })
      );
    } else {
      store.dispatch(setToastOpen({ isOpen: true, message: "Bad Request" }));
    }
  } else if (error?.response?.status == 403) {
    store.dispatch(setToastOpen({ isOpen: true, message:  processError(error?.response?.data) }));
  }
  
  else if (error?.response?.status == 404) {
    store.dispatch(setToastOpen({ isOpen: true, message: "Detail Not Found" }));
  } else {
    store.dispatch(
      setToastOpen({ isOpen: true, message: "An Error Occured!" })
    );
  }

  if (error.response) {
    result.data = error.response.data;
    result.code = error.response.status;
    return result;
  }
  if (error.message) {
    result.data = error.message;
    return result;
  }
};

const successResponse = (response) => {
  const result = {
    success: true,
    data: "null",
    code: 0,
  };

  if (response.data) {
    result.data = response.data;
    result.code = response.status;
    return result;
  } else {
    result.code = response.status;
    return result;
  }
};

export default services;
